// Functions to retrieve the actual url of an external learning tool because d2l hides it

import { LmsThirdPartyQuizWithActivityUrl } from "@verity/api-client/types";
import { getPlatformIdFromUrl } from "@verity/url-matcher";

type D2LTocResponse = {
	Modules: D2LModule[];
};

type D2LTopic = {
	Identifier: string; // String version of TopicId -> TopicId is the quizId
	TypeIdentifier: string; // Link or File
	Title: string; // Name of the external learning tool
	ToolId: number | null; // Links to what kind of tool it is. 39000 is usually external learning tool.
	Url: string; // Quicklink (not the actual url)
};

type D2LModule = {
	Modules: D2LModule[];
	Topics: D2LTopic[];
	IsHidden: boolean;
	ModuleId: number;
};

type ScrapeQuickLinkResult = {
	platformId: number | null;
	activityUrl: string | null;
};

// What is returned back to the frontend
export type D2lExternalLearningTool = {
	quizId: string;
	name: string;
	quickLinkUrl: string;
};

// Makes a fetch request to d2l. Returns all external learning tools. Throws errors
// Done in frontend because backend requires scope permission
export async function fetchD2LTOC(courseId: string): Promise<D2lExternalLearningTool[]> {
	const res = await fetch(`/d2l/api/le/1.61/${courseId}/content/toc`);
	if (!res.ok) throw new Error("Failed to fetch TOC");

	const data = (await res.json()) as D2LTocResponse;
	const externalTools: D2lExternalLearningTool[] = [];

	// D2L Table of contents is recursive
	function walkModules(modules: D2LModule[]) {
		for (const mod of modules) {
			// Check topics in this module
			for (const topic of mod.Topics) {
				if (isExternalLearningTool(topic)) {
					externalTools.push({
						quizId: topic.Identifier,
						name: topic.Title,
						quickLinkUrl: topic.Url,
					});
				}
			}
			// Check sub-modules
			if (mod.Modules.length > 0) {
				walkModules(mod.Modules);
			}
		}
	}

	walkModules(data.Modules);
	return externalTools;
}

function isExternalLearningTool(topic: D2LTopic) {
	return topic.ToolId !== null && topic.TypeIdentifier === "Link";
}

// This tries to find the actual third party platform associated with the external learning tool. Does scraping in multiple fetch requests.
export async function getPlatformIdforExternalLearningTool(
	tools: D2lExternalLearningTool[],
): Promise<LmsThirdPartyQuizWithActivityUrl[]> {
	const results: LmsThirdPartyQuizWithActivityUrl[] = [];
	const BATCH_SIZE = 5;

	for (let i = 0; i < tools.length; i += BATCH_SIZE) {
		const batch = tools.slice(i, i + BATCH_SIZE);

		const batchResults = await Promise.all(
			batch.map(async (tool) => {
				const result = await scrapeQuickLinkUrl(tool);
				return {
					quizId: tool.quizId,
					platformId: result?.platformId ?? null,
					activityUrl: result?.activityUrl ?? null,
				};
			}),
		);

		results.push(...batchResults);

		if (i + BATCH_SIZE < tools.length) {
			await new Promise((resolve) => setTimeout(resolve, 300)); // 300ms delay
		}
	}

	return results;
}

async function scrapeQuickLinkUrl(tool: D2lExternalLearningTool): Promise<ScrapeQuickLinkResult | null> {
	try {
		//  Fetch the QuickLink
		const response = await fetchWithTimeout(tool.quickLinkUrl);
		const html = await response.text();

		let finalUrl: string | null = null;

		const ltiRequestFormRegex = /id=['"]LtiRequestForm['"][^>]+action=['"]([^'"]+)['"]/i;

		// Check if this is the LTI Request Form
		const formMatch = html.match(ltiRequestFormRegex);
		if (formMatch) {
			finalUrl = formMatch[1];
		}
		// Otherwise we gotta find the lti-launch url which will take us to the LTI Request Form
		else {
			const ltiLaunchMatch = html.match(/lti-launch-url="([^"]+)"/i);

			if (ltiLaunchMatch) {
				const launchRes = await fetchWithTimeout(ltiLaunchMatch[1]);
				const launchHtml = await launchRes.text();

				const secondaryFormMatch = launchHtml.match(ltiRequestFormRegex);
				if (secondaryFormMatch) {
					finalUrl = secondaryFormMatch[1];
				}
			}
		}

		return { platformId: getPlatformIdFromUrl(finalUrl), activityUrl: finalUrl };
	} catch (error) {
		return null;
	}
}

async function fetchWithTimeout(url: string, timeoutMs: number = 10000) {
	const controller = new AbortController();
	const id = setTimeout(() => controller.abort(), timeoutMs);

	try {
		const response = await fetch(url, { signal: controller.signal });
		clearTimeout(id);
		return response;
	} catch (error) {
		clearTimeout(id);
		throw error;
	}
}

// Returns DB quiz id for d2l content quiz
export function normalizeD2LContentQuizId(quizId: string) {
	if (quizId.startsWith(`d2lContentQuiz_`)) return quizId;

	return `d2lContentQuiz_${quizId}`;
}

/**
 * Compares two D2L IDs to see if they represent the same quiz,
 * regardless of whether they have the "d2lContentQuiz_" prefix.
 */
export function isD2lContentQuizIdEqual(id1: string | number, id2: string | number): boolean {
	if (id1 === id2) return true;
	if (!id1 || !id2) return false;

	// Convert both to strings and strip the prefix/non-numeric parts
	const clean = (id: string | number) => id.toString().replace("d2lContentQuiz_", "").trim();

	return clean(id1) === clean(id2);
}

/**Returns if a lms quiz id is a d2l content quiz */
export function isD2LContentQuizId(quizId: string): boolean {
	return quizId.startsWith("d2lContentQuiz_");
}
